// SPDX-License-Identifier: Apache-2.0
"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Enclave = void 0;
const ledger_1 = require("../ledger");
const api_1 = require("../api");
const calls_1 = require("../api/calls");
const responses_1 = require("../api/responses");
const typedjson_1 = require("../export/typedjson");
const utils_1 = require("../utils");
const provider_1 = require("./provider");
class Enclave {
    constructor(provider) {
        this.provider = provider;
        this.handlers = new utils_1.EventCache();
        this.internalHandlers = new utils_1.EventCache();
        this.oneShotHandlers = new utils_1.OneShotEventCache();
        this.internalOneShotHandlers = new utils_1.OneShotEventCache();
        this.calls = new Map();
        this.id = 0;
        this.globallySubscribed = false;
        this.individuallySubscribed = new Set();
        this.phaseShiftSubscribed = false;
    }
    static dial(operator) {
        return new Enclave(new provider_1.EnclaveWSProvider(operator));
    }
    isEnclaveWriter() { }
    connect() {
        this.provider.onmessage = (ev) => this.onMessage(ev);
        this.provider.onerror = (ev) => this.onError(ev);
        this.provider.onopen = (ev) => this.onOpen(ev);
        this.provider.onclose = (ev) => this.onClose(ev);
        this.provider.connect();
    }
    disconnect() {
        this.provider.close();
    }
    async onboard(who) {
        const onboard = new calls_1.Onboarding(who);
        await this.sendCall(onboard);
        return;
    }
    async subscribe(who) {
        this.phaseShiftSubscribed = true;
        if (who) {
            this.individuallySubscribed.add(who.key);
        }
        else {
            this.globallySubscribed = true;
        }
        const subTXs = new calls_1.SubscribeTXs(who);
        const subBPs = new calls_1.SubscribeBalanceProofs(who);
        const subPSs = new calls_1.SubscribePhaseShifts();
        await this.sendCall(subTXs);
        await this.sendCall(subBPs);
        await this.sendCall(subPSs);
        return;
    }
    async transfer(tx) {
        return this.sendCall(tx);
    }
    async mint(tx) {
        return this.sendCall(tx);
    }
    async burn(tx) {
        return this.sendCall(tx);
    }
    async trade(tx) {
        return this.sendCall(tx);
    }
    async exit(exitRequest) {
        const p = new Promise((resolve, reject) => {
            this.once_internal("exitproof", resolve);
            this.sendCall(exitRequest).catch(reject);
        });
        return p;
    }
    async getAccount(acc) {
        return this.sendCall(new calls_1.GetAccount(acc));
    }
    async sendCall(data) {
        const id = this.nextID().toString();
        const p = new Promise((resolve, reject) => {
            this.calls.set(id, [resolve, reject]);
        });
        const msg = new api_1.Call(id, data);
        const wiredata = typedjson_1.TypedJSON.stringify(msg, api_1.Call);
        this.provider.send(wiredata);
        return p;
    }
    on(eventType, cb) {
        this.handlers.set(eventType, cb);
    }
    once(eventType, cb) {
        this.oneShotHandlers.set(eventType, cb);
    }
    off(eventType, cb) {
        this.handlers.delete(eventType, cb);
    }
    on_internal(eventType, cb) {
        this.internalHandlers.set(eventType, cb);
    }
    once_internal(eventType, cb) {
        this.internalOneShotHandlers.set(eventType, cb);
    }
    off_internal(eventType, cb) {
        this.internalHandlers.delete(eventType, cb);
    }
    removeAllListeners() {
        this.handlers.clear();
        this.oneShotHandlers.clear();
    }
    nextID() {
        return this.id++;
    }
    onMessage(ev) {
        let om;
        try {
            om = typedjson_1.TypedJSON.parse(ev.data, api_1.Result);
        }
        catch {
            console.info("Received unsupported message");
        }
        const msg = om;
        if (!msg) {
            return this.handleUnknownMessage(ev.data);
        }
        if (msg.id) {
            const [resolve, reject] = this.calls.get(msg.id);
            this.calls.delete(msg.id);
            if (msg.error) {
                reject(new Error(msg.error));
                return this.callEvent("error", msg.error);
            }
            else {
                return resolve(msg.data);
            }
        }
        const obj = msg.data;
        if (obj === undefined) {
            console.info("Received unsupported message from Operator");
            return;
        }
        switch (obj.objectType()) {
            case responses_1.ClientConfig:
                return this.callEvent("config", obj);
            case responses_1.TxReceipt:
                return this.callEvent("receipt", obj);
            case responses_1.BalanceProofs: {
                const bps = obj;
                for (const [_, bp] of bps.map) {
                    if (bp.balance.exit) {
                        this.callEvent("exitproof", bp);
                    }
                    else {
                        this.callEvent("proof", bp);
                    }
                }
                break;
            }
            case responses_1.PhaseShift: {
                const ps = obj;
                this.callEvent("phaseshift", ps);
                break;
            }
            default:
                console.log("Object type: ", obj.objectType());
        }
    }
    callEvent(ev, payload) {
        [
            this.handlers.get(ev),
            this.oneShotHandlers.get(ev),
            this.internalHandlers.get(ev),
            this.internalOneShotHandlers.get(ev),
        ].forEach((cbs) => {
            if (!cbs) {
                return;
            }
            cbs.forEach((f) => {
                f(payload);
            });
        });
    }
    handleUnknownMessage(data) {
        console.error(data);
    }
    onError(ev) {
        console.error("connection error: ", ev);
        this.callEvent("error", new Error("connection error"));
        this.provider.close();
        setTimeout(() => {
            try {
                this.connect();
            }
            catch { }
        }, 1000);
    }
    onOpen(_) {
        const calls = [];
        if (this.globallySubscribed)
            calls.push(new calls_1.SubscribeTXs(), new calls_1.SubscribeBalanceProofs());
        this.individuallySubscribed.forEach((addr) => calls.push(new calls_1.SubscribeTXs(ledger_1.Address.fromString(addr)), new calls_1.SubscribeBalanceProofs(ledger_1.Address.fromString(addr))));
        if (this.phaseShiftSubscribed)
            calls.push(new calls_1.SubscribePhaseShifts());
        this.callEvent("open", {});
        calls.forEach((c) => this.sendCall(c));
    }
    onClose(_) {
        this.callEvent("close", {});
    }
}
exports.Enclave = Enclave;
//# sourceMappingURL=connection.js.map